import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  ArrowRightLeft, 
  ArrowDownLeft, 
  ArrowUpRight, 
  Search, 
  Filter, 
  RefreshCw, 
  Download, 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Building2,
  FileSpreadsheet
} from "lucide-react";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;

const StockMovement = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("All");
  const [warehouseFilter, setWarehouseFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [warehouses, setWarehouses] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  const fetchWarehouses = async () => {
    try {
      const res = await axios.get(`${API}/inventory/warehouses`);
      if (res.data.success) {
        setWarehouses(res.data.warehouses || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        type: typeFilter !== "All" ? typeFilter : undefined,
      };
      const res = await axios.get(`${API}/inventory/activities`, { params });
      if (res.data.success) {
        let acts = res.data.activities || [];

        // In-memory filter for warehouse and search if needed
        if (warehouseFilter !== "All") {
          acts = acts.filter(a => a.warehouse === warehouseFilter || a.warehouseTo === warehouseFilter);
        }
        if (searchTerm.trim()) {
          const s = searchTerm.toLowerCase();
          acts = acts.filter(a => 
            (a.productName || "").toLowerCase().includes(s) ||
            (a.sku || "").toLowerCase().includes(s) ||
            (a.transactionId || "").toLowerCase().includes(s) ||
            (a.referenceId || "").toLowerCase().includes(s)
          );
        }

        setActivities(acts);
        setPagination({
          total: res.data.total || acts.length,
          totalPages: res.data.totalPages || 1,
        });
      }
    } catch (err) {
      console.error("Error fetching stock movements:", err);
      toast.error("Failed to load stock movements");
    } finally {
      setLoading(false);
    }
  }, [page, limit, typeFilter, warehouseFilter, searchTerm]);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleExportCSV = () => {
    if (activities.length === 0) {
      toast.error("No movements to export");
      return;
    }

    const headers = ["Txn ID", "Date", "Product", "SKU", "Type", "Quantity", "Before Qty", "After Qty", "Warehouse", "Warehouse To", "Performed By", "Note"];
    const rows = activities.map(a => [
      `"${a.transactionId || ""}"`,
      `"${new Date(a.createdAt).toISOString()}"`,
      `"${(a.productName || a.product?.name?.en || "").replace(/"/g, '""')}"`,
      `"${a.sku || a.product?.sku || ""}"`,
      `"${a.type || ""}"`,
      a.quantity || 0,
      a.beforeQuantity || 0,
      a.afterQuantity || 0,
      `"${a.warehouse || ""}"`,
      `"${a.warehouseTo || ""}"`,
      `"${a.performedBy || "Admin"}"`,
      `"${(a.note || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `stock_movements_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Movements exported successfully!");
  };

  const getTypeBadge = (type) => {
    if (type === "Stock In" || type === "Receipt") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
          <ArrowDownLeft size={13} /> {type}
        </span>
      );
    }
    if (type === "Stock Out" || type === "Sale") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
          <ArrowUpRight size={13} /> {type}
        </span>
      );
    }
    if (type === "Transfer") {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
          <ArrowRightLeft size={13} /> {type}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
        <FileSpreadsheet size={13} /> {type}
      </span>
    );
  };

  return (
    <section className="min-h-screen bg-[#f3f4f6] p-4 sm:p-6 font-sans">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span>Admin</span>
            <span>›</span>
            <span>Inventory</span>
            <span>›</span>
            <span className="text-[#b07d17] font-semibold">Stock Movement Log</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-philosopher text-gray-900 flex items-center gap-2 font-bold">
            <span className="p-2 rounded-xl bg-gradient-to-br from-[#131921] to-[#232f3e] text-[#febd69] shadow-md">
              <ArrowRightLeft className="w-6 h-6" />
            </span>
            Stock Movement & Transaction Log
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Real-time audit log of stock in, stock out, inter-warehouse transfers, receipts, and inventory adjustments.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchMovements}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700 font-medium shadow-sm transition"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700 font-medium shadow-sm transition"
          >
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search SKU, Product, Txn ID..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
          />
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
        </div>

        {/* Movement Type Filter */}
        <div>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
          >
            <option value="All">All Movement Types</option>
            <option value="Stock In">Stock In (+)</option>
            <option value="Stock Out">Stock Out (-)</option>
            <option value="Transfer">Inter-Warehouse Transfer</option>
            <option value="Adjustment">Adjustment</option>
            <option value="Receipt">Receipt (GRN)</option>
            <option value="Sale">Sale Dispatch</option>
            <option value="Damage">Damage / Loss</option>
            <option value="Return">Return / Replacement</option>
          </select>
        </div>

        {/* Warehouse Filter */}
        <div>
          <select
            value={warehouseFilter}
            onChange={(e) => { setWarehouseFilter(e.target.value); setPage(1); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#b07d17]"
          >
            <option value="All">All Warehouses</option>
            {warehouses.map((wh) => (
              <option key={wh._id} value={wh.name}>
                {wh.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Movement Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#f8f9fa] border-b border-gray-200 text-gray-700">
              <tr>
                <th className="px-4 py-3.5 font-semibold">Date & Time</th>
                <th className="px-4 py-3.5 font-semibold">Transaction ID</th>
                <th className="px-4 py-3.5 font-semibold">Product Title & SKU</th>
                <th className="px-4 py-3.5 font-semibold">Movement Type</th>
                <th className="px-4 py-3.5 font-semibold text-right">Qty Delta</th>
                <th className="px-4 py-3.5 font-semibold text-right">Balance After</th>
                <th className="px-4 py-3.5 font-semibold">Warehouse Path</th>
                <th className="px-4 py-3.5 font-semibold">Performed By & Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-16 text-center text-gray-500">
                    <RefreshCw className="w-8 h-8 animate-spin text-[#b07d17] mx-auto mb-2" />
                    <span>Loading stock movements...</span>
                  </td>
                </tr>
              ) : activities.length > 0 ? (
                activities.map((act) => {
                  const prodName = act.productName || act.product?.name?.en || "--";
                  const sku = act.sku || act.product?.sku || "--";
                  return (
                    <tr key={act._id} className="hover:bg-gray-50/80 transition">
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {new Date(act.createdAt).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-[#007185]">
                        {act.transactionId || "--"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 line-clamp-1 max-w-[220px]" title={prodName}>
                          {prodName}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">SKU: {sku}</div>
                      </td>
                      <td className="px-4 py-3">
                        {getTypeBadge(act.type)}
                      </td>
                      <td className="px-4 py-3 text-right font-extrabold text-sm">
                        <span className={act.quantity > 0 ? "text-emerald-700" : act.quantity < 0 ? "text-rose-700" : "text-gray-700"}>
                          {act.quantity > 0 ? `+${act.quantity}` : act.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800 text-sm">
                        {act.afterQuantity ?? "--"}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        <div className="font-medium">{act.warehouse || "Bhubaneswar (Main)"}</div>
                        {act.warehouseTo && (
                          <div className="text-purple-700 font-semibold text-[11px]">
                            → {act.warehouseTo}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div className="truncate max-w-[180px]" title={act.note}>
                          {act.note || "System log"}
                        </div>
                        <div className="text-[10px] text-gray-400">By: {act.performedBy || "Admin"}</div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-4 py-16 text-center text-gray-500">
                    <ArrowRightLeft className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-base font-semibold text-gray-700">No stock movements recorded</p>
                    <p className="text-xs text-gray-400 mt-1">
                      New transactions like Add Stock, Adjustments or Warehouse Transfers will automatically log here.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && activities.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white text-sm">
            <span className="text-gray-500">
              Showing {activities.length} movement records
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 bg-gray-100 rounded text-xs font-semibold">
                Page {page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.totalPages}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default StockMovement;
